import { useMemo, useRef, useState } from "react";

import {
  type KennismakingAntwoord,
  type KennismakingLijst,
  type KennismakingVraag,
  verstuurKennismaking,
} from "../../public/publicApi";
import { Icoon } from "../../public/DierMatchIcoon";
import { useBovenaan } from "../../public/naarBoven";

/**
 * Het kennismakingsformulier: wat een bezoeker invult nadat hij op "Ik wil ... leren kennen" klikt.
 *
 * <p><b>Dezelfde vorm als de matchwizard.</b> Een bezoeker doorloopt op DierMatch twee keer een rij
 * vragen — eerst om een dier te vinden, dan om er kennis mee te maken — en als die twee er anders
 * uitzien voelt de tweede als een formulier van iemand anders. Daarom staat hier hetzelfde stramien:
 * genummerde stapkop, keuzes als tegels in een raster, en naast de vragen het blok "Waarom vragen we
 * dit?" dat uitlegt waar het antwoord heen gaat. De klassen zijn letterlijk die van de wizard, zodat
 * de twee schermen niet uit elkaar kunnen groeien.
 *
 * <p><b>Stap voor stap en niet alles tegelijk.</b> De lijst telt rond de twintig vragen, en twintig
 * velden onder elkaar schrikt af voordat iemand begonnen is. De secties uit de vragenlijst zijn de
 * stappen; een asiel bepaalt daarmee zelf hoe het formulier loopt, zonder dat hier iets over
 * stapgrootte vastligt.
 *
 * <p><b>De contactvragen staan vooraan en zijn verplicht.</b> Niet uit beleefdheid: zonder
 * bereikbaarheid kan het asiel niets met de inzending, en dan is invullen weggegooide moeite. Wie
 * halverwege afhaakt heeft dan tenminste niets ingevuld dat toch nergens heen kon.
 *
 * <p><b>Er staat bij waar het heen gaat.</b> DierMatch belooft elders dat je geen account nodig
 * hebt en dat er niets van je bewaard wordt; dat gaat over bladeren en bewaren. Zodra je hier je
 * naam en telefoonnummer invult klopt die belofte niet meer vanzelf, dus staat er onderaan wie de
 * gegevens krijgt en waarvoor.
 */
export function DierMatchKennismaking({
  slug,
  lijst,
  dierNaam,
  asielNaam,
  dierFoto = null,
  onSluit,
}: {
  slug: string;
  lijst: KennismakingLijst;
  dierNaam: string;
  asielNaam: string | null;
  /** Het dier zelf, naast de vragen. Zonder foto valt het blok terug op een icoon. */
  dierFoto?: { url: string; alt: string | null } | null;
  onSluit: () => void;
}) {
  const stappen = useMemo(() => groepeer(lijst.vragen, dierNaam), [lijst.vragen, dierNaam]);
  const [stap, setStap] = useState(0);
  const [waarden, setWaarden] = useState<Record<string, string | string[]>>({});
  const [missend, setMissend] = useState<string[]>([]);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [verstuurd, setVerstuurd] = useState(false);

  // Elke stap begint bovenaan -- ook het openen en het slotscherm, want het formulier verschijnt op
  // de plek waar je stond en dat is halverwege de dierpagina. Zie useBovenaan.
  const bovenkant = useRef<HTMLElement | null>(null);
  useBovenaan(bovenkant, `${stap}-${verstuurd}`);

  if (stappen.length === 0) {
    return null;
  }

  if (verstuurd) {
    return (
      <section className="dm-kennis" aria-labelledby="dm-kennis-kop" ref={bovenkant}>
        <div className="dm-kennis-klaar">
          <span className="dm-kennis-vink" aria-hidden="true">
            <Icoon naam="hart" size={28} />
          </span>
          <h2 id="dm-kennis-kop">Je bericht staat klaar voor {asielNaam ?? "het asiel"}</h2>
          <p>
            Ze hebben je antwoorden en je contactgegevens, en nemen zelf contact met je op. Hoe lang
            dat duurt verschilt per asiel — het zijn vaak vrijwilligers.
          </p>
          <p className="dm-kennis-klein">
            Je hebt hier geen account van gekregen en je hoeft niets te onthouden.
          </p>
          <button type="button" className="dm-kennis-verder" onClick={onSluit}>
            Terug naar {dierNaam}
          </button>
        </div>
      </section>
    );
  }

  const huidige = stappen[stap];
  const laatste = stap === stappen.length - 1;
  const info = sectieInfo(huidige.titel, dierNaam, asielNaam);

  const zetWaarde = (vraag: KennismakingVraag, waarde: string | string[]) => {
    setWaarden((vorige) => ({ ...vorige, [vraag.id]: waarde }));
    setMissend((vorige) => vorige.filter((id) => id !== vraag.id));
  };

  const ontbrekend = () =>
    huidige.vragen.filter((v) => v.required && leeg(waarden[v.id])).map((v) => v.id);

  const verder = () => {
    const gaten = ontbrekend();
    if (gaten.length > 0) {
      setMissend(gaten);
      return;
    }
    setStap((s) => s + 1);
  };

  const verstuur = async () => {
    const gaten = ontbrekend();
    if (gaten.length > 0) {
      setMissend(gaten);
      return;
    }
    setBezig(true);
    setFout(null);
    try {
      await verstuurKennismaking(slug, {
        naam: tekst(waarden["contact_naam"]),
        email: tekst(waarden["contact_email"]),
        telefoon: tekst(waarden["contact_telefoon"]),
        plaats: tekst(waarden["contact_plaats"]),
        antwoorden: antwoordenVan(stappen, waarden),
      });
      setVerstuurd(true);
    } catch {
      setFout("Het versturen lukte niet. Probeer het zo nog eens — je antwoorden staan er nog.");
    } finally {
      setBezig(false);
    }
  };

  return (
    <section className="dm-kennis dm-kennis--wizard" aria-labelledby="dm-kennis-kop" ref={bovenkant}>
      <header className="dm-kennis-kop">
        <div>
          <h2 id="dm-kennis-kop">Kennismaken met {dierNaam}</h2>
          <p>
            {asielNaam
              ? `${asielNaam} stelt een paar vragen voordat jullie elkaar ontmoeten.`
              : "Het asiel stelt een paar vragen voordat jullie elkaar ontmoeten."}
          </p>
        </div>
        <button type="button" className="dm-kennis-sluit" onClick={onSluit}>
          Sluiten
        </button>
      </header>

      <Stappenbalk stappen={stappen} huidig={stap} onSpring={setStap} />

      <div className="dm-match-lay">
        <div className="dm-match-kaart">
          <h3 className="dm-kennis-staptitel">
            <span className="dm-match-nr">{stap + 1}.</span> {huidige.titel}
          </h3>
          <p className="dm-match-onder">{info.onder}</p>

          {huidige.vragen.map((vraag) => (
            <VraagBlok
              key={vraag.id}
              vraag={vraag}
              waarde={waarden[vraag.id]}
              mist={missend.includes(vraag.id)}
              onWijzig={(w) => zetWaarde(vraag, w)}
            />
          ))}

          <div className="dm-match-voet">
            <button
              type="button"
              className="dm-match-terug"
              onClick={() => (stap === 0 ? onSluit() : setStap((s) => s - 1))}
            >
              ← {stap === 0 ? "Sluiten" : "Vorige"}
            </button>
            {laatste ? (
              <button type="button" className="dm-match-verder" onClick={verstuur} disabled={bezig}>
                {bezig ? "Bezig met versturen…" : `Versturen naar ${asielNaam ?? "het asiel"}`}
              </button>
            ) : (
              <button type="button" className="dm-match-verder" onClick={verder}>
                Volgende <Icoon naam="pijlRechts" size={17} />
              </button>
            )}
          </div>

          {missend.length > 0 && (
            <p className="dm-match-blokkade" role="alert">
              Vul de velden met een <span aria-hidden="true">*</span> nog even in — anders kan het
              asiel niets met je reactie.
            </p>
          )}
          {fout && (
            <p className="dm-kennis-fout" role="alert">
              {fout}
            </p>
          )}

          <p className="dm-match-tel">
            Stap {stap + 1} van {stappen.length}
          </p>
        </div>

        <Waarom info={info} foto={dierFoto} dierNaam={dierNaam} asielNaam={asielNaam} />
      </div>

      <p className="dm-kennis-klein">
        Je antwoorden en contactgegevens gaan naar {asielNaam ?? "het asiel dat dit dier aanbiedt"} en
        worden alleen gebruikt om contact met je op te nemen over {dierNaam}. Ze worden niet gedeeld
        met andere asielen en je krijgt er geen nieuwsbrief van.
      </p>
    </section>
  );
}

// ── stappenbalk ─────────────────────────────────────────────────────────────────────────────────

/**
 * De secties als bolletjes bovenaan. Terugspringen mag altijd — je antwoorden blijven staan — maar
 * vooruit springen niet: dan sla je precies de verplichte velden over die de volgende stap moet
 * tegenhouden.
 */
function Stappenbalk({
  stappen,
  huidig,
  onSpring,
}: {
  stappen: Stap[];
  huidig: number;
  onSpring: (doel: number) => void;
}) {
  return (
    <nav className="dm-match-balk" aria-label="Voortgang">
      <ol style={{ gridTemplateColumns: `repeat(${stappen.length}, 1fr)` }}>
        {stappen.map((s, i) => {
          const gedaan = i < huidig;
          return (
            <li
              key={s.titel}
              className={`dm-match-stap${i === huidig ? " is-huidig" : ""}${gedaan ? " is-gedaan" : ""}`}
            >
              <button
                type="button"
                onClick={() => onSpring(i)}
                disabled={i > huidig}
                aria-current={i === huidig ? "step" : undefined}
              >
                <span className="dm-match-bol" aria-hidden="true">
                  {gedaan ? "✓" : i + 1}
                </span>
                <span className="dm-match-staplabel">{s.titel}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ── het blok naast de vragen ────────────────────────────────────────────────────────────────────

/**
 * "Waarom vragen we dit?" — met het dier erboven waar het over gaat.
 *
 * <p>Bij de matchwizard staat daar een willekeurig dier uit het aanbod, omdat die wizard nog niet
 * weet welk dier het wordt. Hier weten we dat wel: dit formulier gaat over één dier, en dat hoort
 * ernaast te staan terwijl je over je tuin en je werkdag vertelt.
 */
function Waarom({
  info,
  foto,
  dierNaam,
  asielNaam,
}: {
  info: SectieInfo;
  foto: { url: string; alt: string | null } | null;
  dierNaam: string;
  asielNaam: string | null;
}) {
  return (
    <aside className="dm-match-waarom" aria-labelledby="dm-kennis-waarom">
      {foto ? (
        <figure className="dm-match-waarom-beeld">
          <img src={foto.url} alt={foto.alt ?? dierNaam} loading="lazy" />
          <figcaption>
            {dierNaam}
            {asielNaam && <span> · {asielNaam}</span>}
          </figcaption>
        </figure>
      ) : (
        <span className="dm-match-waarom-icoon">
          <Icoon naam={info.icoon} size={24} />
        </span>
      )}
      <h3 id="dm-kennis-waarom">Waarom vragen we dit?</h3>
      {info.waarom.split("\n\n").map((alinea) => (
        <p key={alinea.slice(0, 24)}>{alinea}</p>
      ))}
    </aside>
  );
}

// ── één vraag ───────────────────────────────────────────────────────────────────────────────────

/**
 * Eén vraag op het scherm. Het type uit de vragenlijst bepaalt de vorm: keuzes worden tegels, tekst
 * blijft tekst.
 */
function VraagBlok({
  vraag,
  waarde,
  mist,
  onWijzig,
}: {
  vraag: KennismakingVraag;
  waarde: string | string[] | undefined;
  mist: boolean;
  onWijzig: (waarde: string | string[]) => void;
}) {
  const id = `dm-kennis-${vraag.id}`;
  const opties = vraag.options ?? [];
  const keuze = vraag.type === "multi" || vraag.type === "single" || vraag.type === "yesno";

  if (keuze && opties.length > 0) {
    const meervoud = vraag.type === "multi";
    // Alles of niets. Eén tegel zonder tekening tussen vier tegels mét valt op als een gat, en dat
    // is erger dan een rij zonder beeld. De vragenlijsten zijn vrije tekst van de asielen, dus een
    // antwoord dat we niet herkennen is geen uitzondering maar het normale geval.
    const herkend = opties.map(optieIcoon);
    const iconen: (string | null)[] = herkend.every((i) => i !== null)
      ? herkend
      : opties.map(() => null);
    // Dezelfde regel als in de matchwizard: het beeld bóven het woord alleen als élke tegel een
    // eigen beeld heeft. Eén tegel zonder icoon in zo'n rij valt op als een gat.
    const gestapeld =
      opties.length >= 3 && iconen.every((i) => i !== null) && opties.every((o) => o.length <= 28);

    return (
      <fieldset className={mist ? "dm-match-vraag is-mist" : "dm-match-vraag"}>
        <legend className="dm-match-vraagtitel" id={id}>
          {vraag.q}
          {vraag.required && (
            <span className="dm-match-verplicht" aria-label="verplicht">
              {" "}
              *
            </span>
          )}
        </legend>
        {vraag.helper && <p className="dm-kennis-hulp">{vraag.helper}</p>}
        <div
          className={gestapeld ? "dm-match-opties dm-match-opties--tegels" : "dm-match-opties"}
          role="group"
          aria-labelledby={id}
        >
          {opties.map((optie, i) => {
            const aan = meervoud
              ? Array.isArray(waarde) && waarde.includes(optie)
              : waarde === optie;
            return (
              <label key={optie} className={aan ? "dm-match-optie is-aan" : "dm-match-optie"}>
                <input
                  type={meervoud ? "checkbox" : "radio"}
                  name={vraag.id}
                  value={optie}
                  checked={aan}
                  onChange={() => {
                    if (!meervoud) {
                      onWijzig(optie);
                      return;
                    }
                    const huidig = Array.isArray(waarde) ? waarde : [];
                    onWijzig(aan ? huidig.filter((o) => o !== optie) : [...huidig, optie]);
                  }}
                  // Een radio die je nog eens aanklikt laat los; zonder dit vangt de browser de
                  // tweede klik af omdat hij al aan staat.
                  onClick={!meervoud && aan ? () => onWijzig("") : undefined}
                />
                {iconen[i] && (
                  <span className="dm-match-optie-icoon">
                    <Icoon naam={iconen[i] as string} size={22} />
                  </span>
                )}
                <span className="dm-match-optie-tekst">
                  <strong>{optie}</strong>
                </span>
                <span className="dm-match-vink" aria-hidden="true">
                  ✓
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }

  return (
    <div
      className={mist ? "dm-match-vraag dm-kennis-veld is-mist" : "dm-match-vraag dm-kennis-veld"}
    >
      <label className="dm-match-vraagtitel" htmlFor={id}>
        {vraag.q}
        {vraag.required && (
          <span className="dm-match-verplicht" aria-hidden="true">
            {" "}
            *
          </span>
        )}
        {vraag.required && <span className="dm-kennis-verborgen"> (verplicht)</span>}
      </label>
      {vraag.helper && <p className="dm-kennis-hulp">{vraag.helper}</p>}
      {invoer(vraag, id, waarde, onWijzig)}
    </div>
  );
}

function invoer(
  vraag: KennismakingVraag,
  id: string,
  waarde: string | string[] | undefined,
  onWijzig: (waarde: string | string[]) => void,
) {
  if (vraag.type === "text") {
    return (
      <textarea
        id={id}
        rows={3}
        value={typeof waarde === "string" ? waarde : ""}
        onChange={(e) => onWijzig(e.target.value)}
      />
    );
  }
  return (
    <div className="dm-match-tekstveld">
      <Icoon naam={veldIcoon(vraag)} size={17} />
      <input
        id={id}
        type={
          vraag.type === "number"
            ? "number"
            : vraag.format === "email"
              ? "email"
              : vraag.format === "tel"
                ? "tel"
                : "text"
        }
        min={vraag.type === "number" ? 0 : undefined}
        inputMode={vraag.type === "number" ? "numeric" : undefined}
        autoComplete={autoAanvullen(vraag)}
        value={typeof waarde === "string" ? waarde : ""}
        onChange={(e) => onWijzig(e.target.value)}
      />
    </div>
  );
}

/** Het icoon vóór een tekstveld — de contactvelden zijn herkenbaar, de rest krijgt een praatwolk. */
function veldIcoon(vraag: KennismakingVraag): string {
  if (vraag.format === "email" || vraag.id === "contact_email") return "envelop";
  if (vraag.format === "tel" || vraag.id === "contact_telefoon") return "telefoon";
  if (vraag.id === "contact_naam") return "persoon";
  if (vraag.id === "contact_plaats") return "speld";
  if (vraag.type === "number") return "klok";
  return "praatwolk";
}

/**
 * Het beeld bij een antwoord, afgeleid uit het antwoord zelf.
 *
 * <p>De vragenlijsten komen van de asielen en zijn gewone tekst; er staat geen icoonnaam in, en die
 * er per vraag bij laten typen zou betekenen dat elk asiel een icoonvocabulaire moet leren. Daarom
 * herkennen we de antwoorden die in de standaardlijsten voorkomen — woningen, diersoorten,
 * huisgenoten, ja/nee — en laat de rest het beeld weg. Een tegel zonder icoon is nog steeds een
 * nette tegel; een tegel met het verkeerde icoon is erger dan geen.
 */
function optieIcoon(optie: string): string | null {
  const t = optie.toLowerCase();

  // Woningen, en wat eraan vast zit. De volgorde is hier de hele truc: "Appartement met balkon" en
  // "Appartement zonder buitenruimte" staan in dezelfde rij, dus de buitenruimte moet eerder gewogen
  // worden dan het woord appartement -- anders krijgen ze weer hetzelfde plaatje.
  if (t.includes("appartement") || t.includes("flat")) {
    const metBuiten = !t.includes("zonder") && (t.includes("balkon") || t.includes("buitenruimte"));
    return metBuiten ? "balkon" : "appartement";
  }
  if (t.includes("tussenwoning")) return "tussenwoning";
  if (t.includes("hoekwoning")) return "hoekwoning";
  if (t.includes("2-onder-1") || t.includes("twee-onder-een")) return "tweeonderkap";
  if (t.includes("boerderij")) return "boerderij";
  if (t.includes("huis") && t.includes("met tuin")) return "huistuin";
  if (t.includes("huis") && t.includes("zonder tuin")) return "vrijstaand";
  if (t.includes("vrijstaand")) return "vrijstaand";

  // Mag je daar dieren houden: een eigen huis is een sleutel, een verhuurder is papierwerk.
  if (t.includes("eigen woning") || t.includes("eigen huis")) return "sleutel";
  if (t.includes("verhuurder") || t.includes("huurcontract")) return "papier";

  // Diersoorten.
  if (t.includes("hond")) return "hond";
  if (t.includes("kat") || t.includes("krabpaal")) return "kat";
  if (t.includes("konijn")) return "konijn";
  if (/cavia|hamster|gerbil|chinchilla|muis|rat|knaagdier/.test(t)) return "knaagdier";
  if (t.includes("vogel") || t.includes("volière") || t.includes("voliere")) return "vogel";

  // Huisgenoten, op leeftijd: een baby, een schoolkind en een tiener zijn drie verschillende
  // antwoorden op dezelfde vraag, dus ook drie verschillende tekeningen.
  if (t.includes("alleen te wonen") || t.includes("woon alleen") || t === "alleen") return "persoon";
  if (t.includes("partner")) return "stel";
  if (t.includes("kinderen onder") || t.includes("baby")) return "gezin";
  if (t.includes("kinderen boven")) return "tiener";
  if (t.includes("kinderen")) return "kind";
  if (t.includes("huisgenoten") || t.includes("bezoek")) return "groep";

  // Ervaring met de soort: nu een, eerder een gehad, of dit wordt de eerste.
  if (t.includes("eerste")) return "kleinetuin";
  if (t.includes("eerder") || t.includes("gehad")) return "klok";
  if (t.includes("heb er nu") || t.includes("houd er al") || t.includes("heb al een")) return "ja";
  if (t.includes("maakt me niet uit") || t.includes("niet van toepassing")) return "vraagteken";

  // Buiten en binnen. Een kat die "binnen en buiten" mag hoort bij de straat waar hij uitkomt: een
  // rustige straat is een boom, een drukke straat is een auto. Dat is precies het verschil waar de
  // vraag om draait.
  if (t.includes("drukke straat") || t.includes("drukke weg")) return "auto";
  if (t.includes("rustige straat") || t.includes("binnen en buiten")) return "tuin";
  if (t.includes("balkon")) return "balkon";
  if (t.includes("omheind") || t.includes("afgeschermd") || t.includes("met een net")) return "hek";
  if (t.includes("geen tuin") || t.includes("geen buitenruimte")) return "binnen";
  if (t.includes("park") || t.includes("grote tuin")) return "tuin";
  if (t.includes("tuin") || t.includes("balkon") || t.includes("buitenruimte")) return "kleinetuin";
  if (t.includes("buitenhok") || t.includes("ren")) return "tuin";
  if (t.includes("binnen") || t.includes("kooi") || t.includes("vrij in huis")) return "binnen";
  if (t.includes("meubel")) return "bank";

  // Ritme en stelligheid.
  if (t.includes("dagelijks") || t.includes("per week") || t.includes("keer")) return "kalender";
  if (t === "ja" || t.startsWith("ja,") || t.includes("geen probleem")) return "ja";
  if (t === "nee" || t.startsWith("nee,") || t.includes("liever niet")) return "nee";
  if (t.includes("weet ik nog niet") || t.includes("navragen") || t.includes("nog niet")) {
    return "vraagteken";
  }
  return null;
}

/**
 * De browser mag naam, e-mail en telefoon aanvullen. Dat scheelt een bezoeker tikwerk op een
 * telefoon, en het zijn precies de velden die hij overal al invult.
 */
function autoAanvullen(vraag: KennismakingVraag): string | undefined {
  if (vraag.format === "email") return "email";
  if (vraag.format === "tel") return "tel";
  if (vraag.id === "contact_naam") return "name";
  if (vraag.id === "contact_plaats") return "address-level2";
  return undefined;
}

interface Stap {
  titel: string;
  vragen: KennismakingVraag[];
}

interface SectieInfo {
  onder: string;
  waarom: string;
  icoon: string;
}

/**
 * De ondertitel en de uitleg naast een stap.
 *
 * <p>De secties komen uit de vragenlijst van het asiel, dus de tekst hangt aan de sectienaam en niet
 * aan een volgnummer: een asiel dat "Jouw huis" vooraan zet krijgt daar dezelfde uitleg bij als een
 * asiel dat hem achteraan zet. De namen hieronder zijn die van de standaardlijsten; een eigen sectie
 * krijgt de algemene tekst, die niets belooft wat niet waar is.
 */
function sectieInfo(titel: string, dierNaam: string, asielNaam: string | null): SectieInfo {
  const asiel = asielNaam ?? "het asiel";
  const t = titel.toLowerCase();

  if (t.startsWith("over jou") || t.includes("contact")) {
    return {
      onder: `Zodat ${asiel} weet wie er reageert.`,
      icoon: "persoon",
      waarom:
        `${asiel} neemt zelf contact met je op — vaak vrijwilligers, soms pas na een paar dagen. ` +
        "Zonder naam en telefoonnummer kunnen ze dat niet, en dan blijft je reactie liggen.\n\n" +
        "Deze velden zijn de enige die echt moeten. Verderop mag je alles overslaan wat je liever " +
        "persoonlijk vertelt.",
    };
  }
  if (t.startsWith("jouw huis") || t.startsWith("je huis") || t.includes("woon")) {
    return {
      onder: `Waar ${dierNaam} terecht zou komen.`,
      icoon: "vrijstaand",
      waarom:
        `${asiel} kent ${dierNaam} en legt jouw antwoorden daarnaast: een kat die nooit buiten ` +
        "kwam, een hond die van een tuin opknapt, een konijn dat een ren nodig heeft.\n\n" +
        "Er is hier geen goed of fout. Eerlijk invullen bespaart jullie allebei een teleurstelling " +
        "achteraf.",
    };
  }
  if (t === `over ${dierNaam.toLowerCase()}`) {
    return {
      onder: `Vragen die ${asiel} speciaal bij ${dierNaam} stelt.`,
      icoon: "hart",
      waarom:
        `Deze vragen staan niet bij elk dier. ${asiel} heeft ze bij ${dierNaam} gezet omdat er iets ` +
        "is dat ze vooraf willen weten.\n\n" +
        "Vaak is dat precies het punt waarop het in een vorig huis misging.",
    };
  }
  if (t.startsWith("over de") || t.startsWith("over het") || t.startsWith("over dit")) {
    return {
      onder: `Wat ${dierNaam} nodig heeft, en of dat past bij jouw dag.`,
      icoon: "poot",
      waarom:
        `De mensen van ${asiel} hebben ${dierNaam} dagelijks om zich heen. Ze weten waar hij van ` +
        "opknapt en waar hij van in de stress schiet, en jouw antwoorden leggen ze daarnaast.\n\n" +
        "Weet je iets nog niet? Zeg dat gerust — daar is een kennismaking voor.",
    };
  }
  if (t.startsWith("tot slot") || t.includes("afsluit")) {
    return {
      onder: "Nog een paar dingen, dan ben je klaar.",
      icoon: "praatwolk",
      waarom:
        `Hierna gaat alles in één keer naar ${asiel}. Iemand leest het door en neemt contact met je ` +
        "op om een afspraak te maken.\n\n" +
        "Je krijgt geen account en geen nieuwsbrief; je hoeft niets te onthouden.",
    };
  }
  return {
    onder: `${asiel} stelt deze vragen zelf.`,
    icoon: "praatwolk",
    waarom:
      `Deze vragen komen van ${asiel}. Je antwoorden gaan rechtstreeks naar hen en worden alleen ` +
      `gebruikt om contact met je op te nemen over ${dierNaam}.`,
  };
}

/**
 * De secties uit de vragenlijst worden de stappen. Een vraag zonder sectie valt in de stap waar hij
 * staat, zodat een half ingevulde lijst van een asiel geen lege stap oplevert.
 */
function groepeer(vragen: KennismakingVraag[], dierNaam: string): Stap[] {
  const stappen: Stap[] = [];
  for (const vraag of vragen) {
    const titel = (vraag.section?.trim() || "Vragen").replaceAll("{naam}", dierNaam);
    const laatste = stappen[stappen.length - 1];
    if (!laatste || laatste.titel !== titel) {
      stappen.push({ titel, vragen: [] });
    }
    stappen[stappen.length - 1].vragen.push({
      ...vraag,
      q: vraag.q.replaceAll("{naam}", dierNaam),
      helper: vraag.helper ? vraag.helper.replaceAll("{naam}", dierNaam) : vraag.helper,
    });
  }
  return stappen;
}

/**
 * Alleen wat ingevuld is gaat mee, mét de vraagtekst erbij. Het asiel mag zijn lijst morgen
 * wijzigen; een antwoord van vandaag moet over een jaar nog leesbaar zijn.
 */
function antwoordenVan(
  stappen: Stap[],
  waarden: Record<string, string | string[]>,
): KennismakingAntwoord[] {
  const uit: KennismakingAntwoord[] = [];
  for (const stap of stappen) {
    for (const vraag of stap.vragen) {
      const waarde = waarden[vraag.id];
      if (leeg(waarde)) {
        continue;
      }
      uit.push({
        veldSleutel: vraag.id,
        sectie: stap.titel,
        vraag: vraag.q,
        antwoord: Array.isArray(waarde) ? waarde.join(", ") : waarde,
      });
    }
  }
  return uit;
}

function leeg(waarde: string | string[] | undefined): boolean {
  if (waarde === undefined) return true;
  return Array.isArray(waarde) ? waarde.length === 0 : waarde.trim() === "";
}

function tekst(waarde: string | string[] | undefined): string {
  if (waarde === undefined) return "";
  return Array.isArray(waarde) ? waarde.join(", ") : waarde;
}
